import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Button,
  Stack,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Badge,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { useApp } from '../context/AppContext';
import TraceResultsModal from './TraceResultsModal';

const CaseNavigation = () => {
  const { caseId, sampleId } = useParams();
  const navigate = useNavigate();
  const { cases, samples, canEditTrace } = useApp();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const currentCase = cases.find(c => String(c.id) === String(caseId));
  const caseSamples = samples.filter(s => String(s.caseId) === String(caseId));

  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const buttonBg = useColorModeValue('primary.50', 'primary.900');
  const buttonHoverBg = useColorModeValue('primary.100', 'primary.800');
  const activeButtonBg = useColorModeValue('primary.500', 'primary.600');
  const activeButtonColor = useColorModeValue('white', 'white');

  if (!currentCase) {
    return null;
  }

  const handleNavigateToSamples = () => {
    // If we're navigating from within a case page, preserve the saved page
    // The page number should already be saved when navigating from CaseManagement
    navigate(`/samples/${caseId}`);
  };

  const handleNavigateToTraces = (targetSampleId = null) => {
    // If we're navigating from within a case page, preserve the saved page
    if (targetSampleId) {
      navigate(`/traces/${caseId}/${targetSampleId}`);
    } else if (caseSamples.length > 0) {
      // Navigate to first sample's traces
      navigate(`/traces/${caseId}/${caseSamples[0].id}`);
    }
  };

  const handleNavigateToTechnicalReport = () => {
    // If we're navigating from within a case page, preserve the saved page
    navigate(`/reports/technical/${caseId}`);
  };

  const handleNavigateToPhotoReport = () => {
    // If we're navigating from within a case page, preserve the saved page
    navigate(`/reports/photo/${caseId}`);
  };

  // Determine current page
  const currentPath = window.location.pathname;
  const isSamplesPage = currentPath.includes('/samples/');
  const isTracesPage = currentPath.includes('/traces/');
  const isTechnicalReportPage = currentPath.includes('/reports/technical/');
  const isPhotoReportPage = currentPath.includes('/reports/photo/');

  return (
    <Box
      bg={bgColor}
      borderBottom="1px solid"
      borderColor={borderColor}
      py={3}
      px={4}
      mb={4}
      sx={{
        '@media print': {
          display: 'none !important',
        },
      }}
    >
      <Flex
        direction={{ base: 'column', md: 'row' }}
        align={{ base: 'flex-start', md: 'center' }}
        justify="space-between"
        gap={4}
      >
        {/* Case Info */}
        <Flex align="center" gap={3}>
          <Text fontWeight="semibold" fontSize="sm" color="gray.600">
            القضية:
          </Text>
          <Badge colorScheme="primary" fontSize="md" dir="ltr">
            {currentCase.caseNumber}
          </Badge>
          {currentCase.caseSubject && (
            <>
              <Text fontSize="sm" color="gray.500" noOfLines={1} maxW="300px">
                {currentCase.caseSubject}
              </Text>
            </>
          )}
        </Flex>

        {/* Navigation Buttons */}
        <Stack
          direction={{ base: 'column', md: 'row' }}
          spacing={2}
          w={{ base: 'full', md: 'auto' }}
          align={{ base: 'stretch', md: 'center' }}
        >
          {/* Samples Button */}
          <Button
            size="sm"
            variant={isSamplesPage ? 'solid' : 'outline'}
            colorScheme="primary"
            onClick={handleNavigateToSamples}
            bg={isSamplesPage ? activeButtonBg : 'transparent'}
            color={isSamplesPage ? activeButtonColor : undefined}
            _hover={{
              bg: isSamplesPage ? activeButtonBg : buttonHoverBg,
            }}
            w={{ base: 'full', md: 'auto' }}
          >
            العينات
          </Button>

          {/* Traces Button with Menu */}
          {caseSamples.length > 0 ? (
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                variant={isTracesPage ? 'solid' : 'outline'}
                colorScheme="primary"
                bg={isTracesPage ? activeButtonBg : 'transparent'}
                color={isTracesPage ? activeButtonColor : undefined}
                _hover={{
                  bg: isTracesPage ? activeButtonBg : buttonHoverBg,
                }}
                w={{ base: 'full', md: 'auto' }}
                rightIcon={
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 10l5 5 5-5z" fill="currentColor"/>
                  </svg>
                }
              >
                الآثار
                {caseSamples.length > 1 && (
                  <Badge ml={2} colorScheme="gray" fontSize="xs">
                    {caseSamples.length}
                  </Badge>
                )}
              </MenuButton>
              <MenuList>
                {caseSamples.map((sample) => (
                  <MenuItem
                    key={sample.id}
                    onClick={() => handleNavigateToTraces(sample.id)}
                    bg={String(sample.id) === String(sampleId) ? buttonBg : undefined}
                    _hover={{ bg: String(sample.id) === String(sampleId) ? buttonBg : undefined }}
                  >
                    <Text dir="ltr">عينة {sample.sampleNumber}</Text>
                    {String(sample.id) === String(sampleId) && (
                      <Badge ml={2} colorScheme="primary" fontSize="xs">
                        الحالية
                      </Badge>
                    )}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          ) : (
            <Button
              size="sm"
              variant="outline"
              colorScheme="primary"
              isDisabled
              title="لا توجد عينات"
              w={{ base: 'full', md: 'auto' }}
            >
              الآثار
            </Button>
          )}

          {/* Bulk Trace Results Entry */}
          <Button
            size="sm"
            variant="outline"
            colorScheme="primary"
            onClick={onOpen}
            isDisabled={caseSamples.length === 0 || !canEditTrace()}
            title={caseSamples.length === 0 ? 'لا توجد عينات' : (!canEditTrace() ? 'غير مسموح' : undefined)}
            w={{ base: 'full', md: 'auto' }}
          >
            تسجيل نتائج الآثار
          </Button>

          {/* Technical Report Button */}
          <Button
            size="sm"
            variant={isTechnicalReportPage ? 'solid' : 'outline'}
            colorScheme="primary"
            onClick={handleNavigateToTechnicalReport}
            bg={isTechnicalReportPage ? activeButtonBg : 'transparent'}
            color={isTechnicalReportPage ? activeButtonColor : undefined}
            _hover={{
              bg: isTechnicalReportPage ? activeButtonBg : buttonHoverBg,
            }}
            w={{ base: 'full', md: 'auto' }}
          >
            التقرير الفني
          </Button>

          {/* Photo Report Button */}
          <Button
            size="sm"
            variant={isPhotoReportPage ? 'solid' : 'outline'}
            colorScheme="primary"
            onClick={handleNavigateToPhotoReport}
            bg={isPhotoReportPage ? activeButtonBg : 'transparent'}
            color={isPhotoReportPage ? activeButtonColor : undefined}
            _hover={{
              bg: isPhotoReportPage ? activeButtonBg : buttonHoverBg,
            }}
            w={{ base: 'full', md: 'auto' }}
          >
            التقرير المصور
          </Button>
        </Stack>
      </Flex>

      <TraceResultsModal caseId={caseId} isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};

export default CaseNavigation;

